import React from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { styled, alpha } from '@mui/material/styles';
import { MenuItemLink } from 'react-admin';
import { Box, Typography, Tooltip } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// Top-level menu item link (both expanded and collapsed rail mode)
export const StyledMenuItemLink = styled(MenuItemLink, {
    shouldForwardProp: (prop) => prop !== 'sidebarOpen',
})(({ theme, sidebarOpen }) => ({
    position: 'relative',
    transition: 'all 0.18s ease',
    textDecoration: 'none',
    boxSizing: 'border-box',

    ...(sidebarOpen ? {
        display: 'flex',
        alignItems: 'center',
        borderRadius: '6px',
        margin: '1px 0',
        padding: '6.5px 8px !important',
        minHeight: '34px',
        color: theme.palette.text.primary,
        position: 'relative',
        '&:hover': {
            backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.06)
                : alpha(theme.palette.common.black, 0.04),
        },
        '& .RaMenuItemLink-icon, & .MuiListItemIcon-root': {
            minWidth: 'unset !important',
            width: '19px !important',
            flex: '0 0 19px !important',
            color: theme.palette.mode === 'dark' ? '#90caf9' : '#5B8CC4',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '10px !important',
            '& svg': {
                fontSize: '19px !important',
            },
        },
        '& .MuiTypography-root, & .MuiListItemText-primary': {
            fontSize: '13.5px !important',
            fontWeight: 400,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: 'inherit',
            lineHeight: 1.4,
            width: '100%',
            minWidth: 0,
        },
        // Active item styling (matches prototype .it.on and Screenshot 2)
        '&.RaMenuItemLink-active, &.active, &.Mui-selected': {
            backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.primary.main, 0.2)
                : '#EAF0F7 !important',
            color: theme.palette.mode === 'dark' ? '#90caf9' : '#123B6B !important',
            fontWeight: '500 !important',
            '&::before': {
                content: '""',
                position: 'absolute',
                left: '-8px',
                top: '6px',
                bottom: '6px',
                width: '3px',
                borderRadius: '0 3px 3px 0',
                backgroundColor: theme.palette.mode === 'dark' ? '#90caf9' : '#1B4D89',
            },
            '& .RaMenuItemLink-icon, & .MuiListItemIcon-root': {
                color: theme.palette.mode === 'dark' ? '#90caf9' : '#1B4D89 !important',
            },
            '& .MuiTypography-root, & .MuiListItemText-primary': {
                color: theme.palette.mode === 'dark' ? '#90caf9' : '#123B6B !important',
                fontWeight: '500 !important',
            },
            '& [data-nav-badge="info"]': {
                backgroundColor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.common.white, 0.15)
                    : '#FFFFFF',
                boxShadow: theme.palette.mode === 'dark'
                    ? 'none'
                    : '0 1px 2px rgba(18, 59, 107, 0.08)',
            },
        },
    } : {
        // Collapsed rail mode (matches Screenshot 2)
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '8px',
        width: '44px',
        height: '44px',
        minWidth: '44px',
        maxWidth: '44px',
        margin: '4px auto',
        padding: '0 !important',
        overflow: 'hidden',
        color: theme.palette.mode === 'dark' ? '#90caf9' : '#4b78b4',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
        '& .RaMenuItemLink-icon, & .MuiListItemIcon-root': {
            minWidth: 'unset !important',
            width: '100% !important',
            margin: '0 !important',
            padding: '0 !important',
            color: 'inherit',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            '& svg': {
                fontSize: '1.35rem',
            },
        },
        '& .MuiTypography-root, & .MuiListItemText-root, & .RaMenuItemLink-text': {
            display: 'none !important',
        },
        // Active in collapsed rail (Screenshot 2: rounded blue box with left vertical blue stripe)
        '&.RaMenuItemLink-active, &.active, &.Mui-selected': {
            backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.primary.main, 0.22)
                : alpha(theme.palette.primary.main, 0.12),
            color: theme.palette.primary.main,
            '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: '6px',
                bottom: '6px',
                width: '3px',
                borderRadius: '0 3px 3px 0',
                backgroundColor: theme.palette.primary.main,
            },
            '& .RaMenuItemLink-icon, & .MuiListItemIcon-root': {
                color: theme.palette.primary.main,
            },
        },
    }),
}));

// Legacy alias for top-level single items
export const StyledSingleMenuItemLink = StyledMenuItemLink;

// Sub-item tree guide line container
export const NavSubTree = styled('div')(({ theme }) => ({
    margin: '0 0 2px 18px',
    borderLeft: `1px solid ${theme.palette.divider}`,
    padding: '2px 0 4px 0',
    display: 'flex',
    flexDirection: 'column',
}));

// Helpers to determine if a sub-item link matches current location (including search queries)
const parseJsonSafely = (str) => {
    try {
        return JSON.parse(str);
    } catch {
        return null;
    }
};

const areParamValuesMatching = (targetVal, currentVal) => {
    if (!currentVal) return false;

    const targetObj = parseJsonSafely(targetVal);
    const currentObj = parseJsonSafely(currentVal);

    if (targetObj && currentObj && typeof targetObj === 'object') {
        return Object.keys(targetObj).every((k) => targetObj[k] === currentObj[k]);
    }

    return targetVal === currentVal;
};

const isSearchMatching = (targetSearch, currentSearch) => {
    if (!targetSearch) return true;

    try {
        const cleanTarget = targetSearch.startsWith('?') ? targetSearch.slice(1) : targetSearch;
        const cleanCurrent = currentSearch.startsWith('?') ? currentSearch.slice(1) : currentSearch;
        const targetParams = new URLSearchParams(cleanTarget);
        const currentParams = new URLSearchParams(cleanCurrent);

        for (const [key, targetVal] of targetParams.entries()) {
            if (!areParamValuesMatching(targetVal, currentParams.get(key))) {
                return false;
            }
        }

        return true;
    } catch {
        return false;
    }
};

const isItemActive = (to, location) => {
    if (!to || !location) return false;

    const targetPathname = typeof to === 'string' ? to : to.pathname || '';
    const targetSearch = typeof to === 'object' ? to.search || '' : '';

    const isPathMatch = targetPathname === '/'
        ? location.pathname === '/'
        : location.pathname === targetPathname || location.pathname.startsWith(`${targetPathname}/`);

    if (!isPathMatch) return false;

    return isSearchMatching(targetSearch, location.search);
};

const SubMenuItemLinkRoot = styled(Link, {
    shouldForwardProp: (prop) => prop !== 'isActive',
})(({ theme, isActive }) => ({
    display: 'block',
    width: '100%',
    padding: '5.5px 8px 5.5px 14px',
    margin: '1px 0',
    textAlign: 'left',
    borderRadius: '0 6px 6px 0',
    color: theme.palette.text.secondary,
    position: 'relative',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minHeight: '28px',
    textDecoration: 'none',
    boxSizing: 'border-box',
    fontSize: '13px',
    lineHeight: 1.4,
    fontFamily: theme.typography.fontFamily,
    transition: 'all 0.15s ease',
    '&:hover': {
        backgroundColor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.common.white, 0.05)
            : alpha(theme.palette.common.black, 0.03),
        color: theme.palette.text.primary,
    },
    ...(isActive && {
        backgroundColor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.primary.main, 0.2)
            : '#EAF0F7 !important',
        color: theme.palette.mode === 'dark' ? '#90caf9' : '#123B6B !important',
        fontWeight: '500 !important',
        '&::before': {
            content: '""',
            position: 'absolute',
            left: '-1px',
            top: '4px',
            bottom: '4px',
            width: '2px',
            backgroundColor: theme.palette.mode === 'dark' ? '#90caf9' : '#1B4D89',
        },
    }),
}));

// Nested child menu item link (indented under tree guide)
export const StyledSubMenuItemLink = React.forwardRef(({
    primaryText,
    children,
    to,
    onClick,
    className,
    ...rest
}, ref) => {
    const location = useLocation();
    const active = isItemActive(to, location);

    return (
        <SubMenuItemLinkRoot
            ref={ref}
            to={to}
            isActive={active}
            className={`${className || ''} ${active ? 'RaMenuItemLink-active active is-active' : ''}`.trim()}
            onClick={onClick}
            {...rest}
        >
            {primaryText || children}
        </SubMenuItemLinkRoot>
    );
});

StyledSubMenuItemLink.displayName = 'StyledSubMenuItemLink';

StyledSubMenuItemLink.propTypes = {
    primaryText: PropTypes.node,
    children: PropTypes.node,
    to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    onClick: PropTypes.func,
    className: PropTypes.string,
};

// Section header (collapsible group header in expanded mode, dash in collapsed mode)
export const SectionHeaderContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'sidebarOpen',
})(({ theme, sidebarOpen }) => ({
    ...(sidebarOpen ? {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '9px 8px 5px 8px',
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover .section-title': {
            color: theme.palette.text.primary,
        },
        '&:hover .section-icon': {
            color: theme.palette.text.primary,
        },
    } : {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px 0 4px',
        '&::after': {
            content: '""',
            width: '16px',
            height: '1px',
            backgroundColor: theme.palette.divider,
        },
    }),
}));

export const SectionHeader = ({ title, isOpen, onToggle, sidebarOpen }) => {
    if (!sidebarOpen) {
        return (
            <Tooltip placement="right" title={title}>
                <SectionHeaderContainer sidebarOpen={false} />
            </Tooltip>
        );
    }

    return (
        <SectionHeaderContainer sidebarOpen role="button" tabIndex={0} onClick={onToggle}>
            <Typography
                className="section-title"
                variant="caption"
                sx={{
                    fontSize: '10.5px',
                    fontWeight: 600,
                    letterSpacing: '0.085em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    transition: 'color 0.15s ease',
                }}
            >
                {title}
            </Typography>
            <KeyboardArrowDownIcon
                className="section-icon"
                sx={{
                    fontSize: '16px',
                    color: 'text.secondary',
                    transition: 'transform 0.2s ease, color 0.15s ease',
                    transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                }}
            />
        </SectionHeaderContainer>
    );
};

SectionHeader.propTypes = {
    title: PropTypes.string.isRequired,
    isOpen: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
    sidebarOpen: PropTypes.bool.isRequired,
};

// Pill Badge component for counters (e.g. Alerts 2, Requests 12)
const NavBadgeRoot = styled('span', {
    shouldForwardProp: (prop) => prop !== 'tone',
})(({ theme, tone }) => {
    const isWarning = tone === 'warning';
    return {
        fontSize: '0.6875rem',
        fontWeight: 600,
        padding: '1px 6px',
        borderRadius: '99px',
        marginLeft: 'auto',
        lineHeight: 1.4,
        flexShrink: 0,
        backgroundColor: isWarning
            ? (theme.palette.mode === 'dark' ? alpha(theme.palette.warning.main, 0.25) : '#FBF1DE')
            : (theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.2) : '#EAF0F7'),
        color: isWarning
            ? (theme.palette.mode === 'dark' ? theme.palette.warning.light : '#8A5906')
            : (theme.palette.mode === 'dark' ? '#90caf9' : theme.palette.primary.main),
    };
});

export const NavBadge = ({ tone = 'info', children, ...props }) => (
    <NavBadgeRoot data-nav-badge={tone} tone={tone} {...props}>
        {children}
    </NavBadgeRoot>
);

NavBadge.propTypes = {
    tone: PropTypes.string,
    children: PropTypes.node,
};

// Menu Section wrapper
export const MenuSection = styled('div')({
    marginBottom: '2px',
});
